import { useState, useEffect, useCallback } from 'react';

import { keyframes } from '@mui/system';
import {
  Box,
  Paper,
  Stack,
  Avatar,
  Button,
  Dialog,
  Tooltip,
  Typography,
  IconButton,
  DialogContent,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  InputAdornment,
} from '@mui/material';

import { useTranslate } from 'src/locales';

import { Iconify } from 'src/components/iconify';

import { useWhatsAppCallContext } from '../hooks/use-whatsapp-call-context';

// ----------------------------------------------------------------------

const TERMINAL_STATUSES = new Set(['ENDED', 'FAILED', 'REJECTED', 'MISSED']);

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(34, 197, 94, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
`;

export function WhatsAppCallPopup() {
  const { t } = useTranslate('translation');
  const {
    primaryCall,
    acceptCall,
    rejectCall,
    hangUp,
    toggleMute,
    isMuted,
    toggleHold,
    isHeld,
    selectedMicrophoneId,
    setSelectedMicrophoneId,
    microphones,
  } = useWhatsAppCallContext();

  const [elapsedSec, setElapsedSec] = useState(0);
  // Ongoing call sent to the background (floating bar) so the agent can keep working
  const [minimized, setMinimized] = useState(false);

  // Robust i18n fallback translation utility to avoid raw keys when locales are not configured
  const getTranslation = useCallback((key: string, fallback: string): string => {
    const val = t(key);
    return val === key ? fallback : val;
  }, [t]);

  useEffect(() => {
    if (primaryCall?.status !== 'ACTIVE' || !primaryCall?.connectedAt) {
      setElapsedSec(0);
      return undefined;
    }

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - (primaryCall.connectedAt || Date.now())) / 1000);
      setElapsedSec(elapsed >= 0 ? elapsed : 0);
    }, 1000);

    return () => clearInterval(interval);
  }, [primaryCall?.status, primaryCall?.connectedAt]);

  const formatTime = (totalSec: number) => {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const isOpen = Boolean(primaryCall);
  const isRinging = primaryCall?.status === 'RINGING';
  const isActive = primaryCall?.status === 'ACTIVE';
  const isTerminal = primaryCall?.status ? TERMINAL_STATUSES.has(primaryCall.status) : false;
  const callId = primaryCall?.callId ?? '';
  const showHangUp = !isRinging || primaryCall?.direction === 'OUTBOUND';
  const isIncomingRinging = isRinging && primaryCall?.direction === 'INBOUND';
  // Only an ongoing call can go to the background; incoming ringing and ended calls stay in front
  const canMinimize = Boolean(primaryCall) && !isIncomingRinging && !isTerminal;

  useEffect(() => {
    if (!canMinimize) {
      setMinimized(false);
    }
  }, [canMinimize]);

  if (!primaryCall) {
    return null;
  }

  const displayName = primaryCall.contactName || primaryCall.from || getTranslation('WHATSAPP_CALL_UNKNOWN', 'Unknown caller');
  const showIncomingLabel = isRinging && primaryCall.direction === 'INBOUND';

  const hangUpButton = (
    <IconButton
      onClick={() => hangUp(callId)}
      sx={{
        bgcolor: 'error.main',
        color: 'error.contrastText',
        '&:hover': { bgcolor: 'error.dark' },
        width: 56,
        height: 56,
      }}
      aria-label={getTranslation('WHATSAPP_CALL_HANG_UP', 'Hang up')}
    >
      <Iconify icon="solar:phone-bold" width={28} sx={{ transform: 'rotate(135deg)' }} />
    </IconButton>
  );

  // Outbound CONNECTING before pickup is shown as ringing
  const displayStatus =
    primaryCall.direction === 'OUTBOUND' &&
    primaryCall.status === 'CONNECTING' &&
    !primaryCall.connectedAt
      ? 'RINGING'
      : primaryCall.status;
  const statusLabel = isActive
    ? formatTime(elapsedSec)
    : getTranslation(
        `WHATSAPP_CALL_STATUS_${displayStatus}`,
        `${displayStatus.charAt(0)}${displayStatus.slice(1).toLowerCase()}...`
      );

  if (minimized) {
    return (
      <Paper
        elevation={12}
        sx={{
          position: 'fixed',
          top: 12,
          // Symmetric centering so it stays centered in RTL too
          left: 0,
          right: 0,
          mx: 'auto',
          width: 440,
          maxWidth: 'calc(100vw - 32px)',
          zIndex: (theme) => theme.zIndex.modal,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pl: 1,
          pr: 1,
          py: 1,
          borderRadius: 6,
          border: '1px solid rgba(84, 214, 44, 0.45)',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(28, 28, 28, 0.96)' : 'rgba(255, 255, 255, 0.97)',
          // Tint starts from the contact side: left in LTR, right in RTL (gradients aren't auto-flipped)
          backgroundImage: (theme) =>
            `linear-gradient(${theme.direction === 'rtl' ? 270 : 90}deg, rgba(84, 214, 44, 0.1), rgba(84, 214, 44, 0) 60%)`,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
        }}
      >
        {/* Contact + status (click to reopen the full popup) */}
        <Tooltip title={getTranslation('WHATSAPP_CALL_EXPAND', 'Open call')}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            onClick={() => setMinimized(false)}
            sx={{ cursor: 'pointer', minWidth: 0, flexGrow: 1, pl: 0.5 }}
          >
            <Box sx={{ position: 'relative', flexShrink: 0 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'success.lighter',
                  animation: !isActive ? `${pulse} 1.6s infinite ease-in-out` : 'none',
                }}
              >
                {primaryCall.avatarUrl ? (
                  <Avatar src={primaryCall.avatarUrl} alt={displayName} sx={{ width: 44, height: 44 }} />
                ) : (
                  <Iconify icon="logos:whatsapp-icon" width={24} />
                )}
              </Box>
              {primaryCall.avatarUrl && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Iconify icon="logos:whatsapp-icon" width={12} />
                </Box>
              )}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                {displayName}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    flexShrink: 0,
                    borderRadius: '50%',
                    bgcolor: isActive && isHeld(callId) ? 'warning.main' : 'success.main',
                    animation: !isActive ? `${pulse} 1.6s infinite ease-in-out` : 'none',
                  }}
                />
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    fontVariantNumeric: 'tabular-nums',
                    color: isActive && isHeld(callId) ? 'warning.main' : 'success.main',
                  }}
                >
                  {isActive && isHeld(callId)
                    ? getTranslation('WHATSAPP_CALL_HELD_LABEL', 'On Hold')
                    : statusLabel}
                </Typography>
                {isActive && isMuted(callId) && (
                  <Typography variant="caption" noWrap sx={{ fontWeight: 600, color: 'error.main' }}>
                    • {getTranslation('WHATSAPP_CALL_MUTED_LABEL', 'Microphone Muted')}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </Tooltip>

        {/* Call controls */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
          {isActive && (
            <Tooltip
              title={isHeld(callId) ? getTranslation('RESUME', 'Resume') : getTranslation('HOLD', 'Hold')}
            >
              <IconButton
                onClick={() => toggleHold(callId)}
                sx={{
                  bgcolor: isHeld(callId) ? 'warning.main' : 'action.selected',
                  color: isHeld(callId) ? 'warning.contrastText' : 'text.primary',
                  '&:hover': { bgcolor: isHeld(callId) ? 'warning.dark' : 'action.hover' },
                  width: 38,
                  height: 38,
                }}
              >
                <Iconify icon={isHeld(callId) ? 'solar:play-bold' : 'solar:pause-bold'} width={18} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={isMuted(callId) ? getTranslation('UNMUTE', 'Unmute') : getTranslation('MUTE', 'Mute')}>
            <IconButton
              onClick={() => toggleMute(callId)}
              sx={{
                bgcolor: isMuted(callId) ? 'error.main' : 'action.selected',
                color: isMuted(callId) ? 'error.contrastText' : 'text.primary',
                '&:hover': { bgcolor: isMuted(callId) ? 'error.dark' : 'action.hover' },
                width: 38,
                height: 38,
              }}
            >
              <Iconify icon={isMuted(callId) ? 'ep:mute' : 'solar:microphone-bold'} width={18} />
            </IconButton>
          </Tooltip>

          <Tooltip title={getTranslation('WHATSAPP_CALL_EXPAND', 'Open call')}>
            <IconButton
              onClick={() => setMinimized(false)}
              sx={{
                bgcolor: 'action.selected',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
                width: 38,
                height: 38,
              }}
            >
              <Iconify icon="material-symbols:open-in-full-rounded" width={16} />
            </IconButton>
          </Tooltip>

          <Tooltip title={getTranslation('WHATSAPP_CALL_HANG_UP', 'Hang up')}>
            <IconButton
              onClick={() => hangUp(callId)}
              sx={{
                bgcolor: 'error.main',
                color: 'error.contrastText',
                '&:hover': { bgcolor: 'error.dark' },
                width: 44,
                height: 38,
                borderRadius: 3,
              }}
            >
              <Iconify icon="solar:phone-bold" width={18} sx={{ transform: 'rotate(135deg)' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>
    );
  }

  return (
    <Dialog
      open={isOpen}
      maxWidth="xs"
      fullWidth
      // Clicking outside / Esc sends an ongoing call to the background instead of blocking the app
      onClose={() => {
        if (canMinimize) setMinimized(true);
      }}
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          overflow: 'visible', // Allow dropdown/elements to render without boundary clipping
          // Premium glassmorphism style
          backdropFilter: 'blur(10px)',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(28, 28, 28, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          boxShadow: (theme) => theme.customShadows?.dialog || theme.shadows[24],
        },
      }}
    >
      <Box sx={{ px: 4, pt: 4, pb: 4, position: 'relative' }}>
        {canMinimize && (
          <Tooltip title={getTranslation('WHATSAPP_CALL_MINIMIZE', 'Minimize')}>
            <IconButton
              size="small"
              onClick={() => setMinimized(true)}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 32,
                height: 32,
                bgcolor: 'action.selected',
                color: 'text.secondary',
                '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
              }}
            >
              <Iconify icon="material-symbols:close-fullscreen-rounded" width={16} />
            </IconButton>
          </Tooltip>
        )}

        <Stack spacing={3} alignItems="center" sx={{ pb: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                width: 76,
                height: 76,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isTerminal ? 'action.selected' : 'success.lighter',
                color: isTerminal ? 'text.disabled' : 'success.main',
                // Pulsing expanding ripple ring animation
                animation:
                  (!isTerminal && (isRinging || primaryCall.status === 'DIALING' || primaryCall.status === 'CONNECTING'))
                    ? `${pulse} 1.6s infinite ease-in-out`
                    : 'none',
                filter: isTerminal ? 'grayscale(100%)' : 'none',
                opacity: isTerminal ? 0.6 : 1,
                transition: 'all 0.3s ease',
              }}
            >
              {primaryCall.avatarUrl ? (
                <Avatar
                  src={primaryCall.avatarUrl}
                  alt={displayName}
                  sx={{
                    width: 76,
                    height: 76,
                    filter: isTerminal ? 'grayscale(100%)' : 'none',
                  }}
                />
              ) : (
                <Iconify icon="logos:whatsapp-icon" width={44} />
              )}
            </Box>

            {primaryCall.avatarUrl && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  bgcolor: (theme) => theme.palette.background.paper,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: (theme) => theme.shadows[2],
                }}
              >
                <Iconify icon="logos:whatsapp-icon" width={16} />
              </Box>
            )}
          </Box>

          <Stack spacing={0.5} alignItems="center">
            <Typography variant="h6" textAlign="center" sx={{ fontWeight: 700 }}>
              {displayName}
            </Typography>

            {/* Display ticking timer when active */}
            {isActive && (
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                {formatTime(elapsedSec)}
              </Typography>
            )}

            {/* Display dynamic mute visual badge */}
            {isActive && isMuted(callId) && !isHeld(callId) && (
              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                {getTranslation('WHATSAPP_CALL_MUTED_LABEL', 'Microphone Muted')}
              </Typography>
            )}

            {/* Display dynamic hold visual badge */}
            {isActive && isHeld(callId) && (
              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 700 }}>
                {getTranslation('WHATSAPP_CALL_HELD_LABEL', 'On Hold')}
              </Typography>
            )}

            {/* Call ended or failed status */}
            {isTerminal && (
              <Typography
                variant="body2"
                color={primaryCall.status === 'FAILED' ? 'error.main' : 'text.secondary'}
                sx={{ fontWeight: 700, mt: 0.5 }}
              >
                {primaryCall.status === 'FAILED'
                  ? getTranslation('WHATSAPP_CALL_FAILED_LABEL', 'Call Failed')
                  : getTranslation('WHATSAPP_CALL_ENDED_LABEL', 'Call Ended')}
              </Typography>
            )}

            {/* Inbound Ringing status label */}
            {showIncomingLabel && (
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                {getTranslation('WHATSAPP_CALL_INCOMING', 'Incoming WhatsApp call')}
              </Typography>
            )}

            {/* Dialing/connecting status label */}
            {!isActive && !isRinging && !isTerminal && (
              <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                {statusLabel}
              </Typography>
            )}
          </Stack>

          {isTerminal ? (
            <Button
              variant="contained"
              onClick={() => hangUp(callId)}
              sx={{
                borderRadius: 1.5,
                px: 4,
                py: 1,
                bgcolor: 'text.primary',
                color: 'background.paper',
                '&:hover': {
                  bgcolor: 'text.secondary',
                },
              }}
            >
              {getTranslation('CLOSE', 'Close')}
            </Button>
          ) : isRinging && primaryCall.direction === 'INBOUND' ? (
            <Stack direction="row" spacing={2} sx={{ pt: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<Iconify icon="solar:phone-bold" />}
                onClick={() => acceptCall(callId)}
                sx={{ borderRadius: 1.5, px: 3, py: 1 }}
              >
                {getTranslation('WHATSAPP_CALL_ANSWER', 'Answer')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Iconify icon="solar:phone-bold-duotone" />}
                onClick={() => rejectCall(callId)}
                sx={{ borderRadius: 1.5, px: 3, py: 1 }}
              >
                {getTranslation('WHATSAPP_CALL_DECLINE', 'Decline')}
              </Button>
            </Stack>
          ) : showHangUp ? (
            <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center" sx={{ pt: 1 }}>
              {/* Keypad (Visual/Mock) - hidden for now
              <Tooltip title={getTranslation('KEYPAD', 'Keypad')}>
                <span>
                  <IconButton
                    disabled
                    sx={{
                      bgcolor: 'action.selected',
                      color: 'text.disabled',
                      width: 44,
                      height: 44,
                      opacity: 0.5,
                    }}
                  >
                    <Iconify icon="famicons:keypad-sharp" width={20} />
                  </IconButton>
                </span>
              </Tooltip>
              */}

              {/* Mute Button */}
              <Tooltip title={isMuted(callId) ? getTranslation('UNMUTE', 'Unmute') : getTranslation('MUTE', 'Mute')}>
                <IconButton
                  onClick={() => toggleMute(callId)}
                  sx={{
                    bgcolor: isMuted(callId) ? 'error.main' : 'action.selected',
                    color: isMuted(callId) ? 'error.contrastText' : 'text.primary',
                    '&:hover': {
                      bgcolor: isMuted(callId) ? 'error.dark' : 'action.hover',
                    },
                    width: 44,
                    height: 44,
                  }}
                >
                  <Iconify
                    icon={isMuted(callId) ? 'ep:mute' : 'solar:microphone-bold'}
                    width={20}
                  />
                </IconButton>
              </Tooltip>

              {/* Hold Button */}
              <Tooltip title={isHeld(callId) ? getTranslation('RESUME', 'Resume') : getTranslation('HOLD', 'Hold')}>
                <IconButton
                  onClick={() => toggleHold(callId)}
                  sx={{
                    bgcolor: isHeld(callId) ? 'warning.main' : 'action.selected',
                    color: isHeld(callId) ? 'warning.contrastText' : 'text.primary',
                    '&:hover': {
                      bgcolor: isHeld(callId) ? 'warning.dark' : 'action.hover',
                    },
                    width: 44,
                    height: 44,
                  }}
                >
                  <Iconify
                    icon={isHeld(callId) ? 'solar:play-bold' : 'solar:pause-bold'}
                    width={20}
                  />
                </IconButton>
              </Tooltip>

              {/* Transfer (Visual/Mock) - hidden for now
              <Tooltip title={getTranslation('TRANSFER', 'Transfer')}>
                <span>
                  <IconButton
                    disabled
                    sx={{
                      bgcolor: 'action.selected',
                      color: 'text.disabled',
                      width: 44,
                      height: 44,
                      opacity: 0.5,
                    }}
                  >
                    <Iconify icon="mingcute-transfer-line" width={20} />
                  </IconButton>
                </span>
              </Tooltip>
              */}

              {/* End Call Button */}
              <Tooltip title={getTranslation('WHATSAPP_CALL_HANG_UP', 'Hang up')}>
                <IconButton
                  onClick={() => hangUp(callId)}
                  sx={{
                    bgcolor: 'error.main',
                    color: 'error.contrastText',
                    '&:hover': { bgcolor: 'error.dark' },
                    width: 44,
                    height: 44,
                  }}
                >
                  <Iconify icon="solar:phone-bold" width={22} sx={{ transform: 'rotate(135deg)' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null}

          {/* Premium Microphone Selection Dropdown */}
          {microphones.length > 0 && !isTerminal && (
            <FormControl size="small" sx={{ mt: 1, width: '100%', maxWidth: 240 }}>
              <InputLabel id="wa-mic-select-label">{getTranslation('WHATSAPP_CALL_MICROPHONE', 'Microphone')}</InputLabel>
              <Select
                labelId="wa-mic-select-label"
                value={selectedMicrophoneId}
                label={getTranslation('WHATSAPP_CALL_MICROPHONE', 'Microphone')}
                onChange={(e) => setSelectedMicrophoneId(e.target.value)}
                startAdornment={
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <Iconify icon="solar:microphone-bold" width={16} sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                }
                sx={{
                  borderRadius: 1.5,
                  width:"100%",
                  fontSize: '0.8125rem',
                  '& .MuiSelect-select': {
                    py: 1,
                    pr: '32px !important', // Prevent text from overlapping the dropdown arrow
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                {microphones.map((mic) => (
                  <MenuItem key={mic.deviceId} value={mic.deviceId} sx={{ fontSize: '0.8125rem' }}>
                    {mic.label || `Microphone ${mic.deviceId.slice(0, 5)}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
}
